import { CONVEYANCE_TYPES } from '@koala/sdk';
import {
  forgotPassword,
  register,
  resetPassword,
  logout,
  validateLoyaltyToken,
  validateBasket,
} from '@koala/sdk/v4';
import Router from 'next/router';
import {
  call,
  delay,
  put,
  race,
  take,
  takeLatest,
  select,
  type SagaReturnType,
} from 'redux-saga/effects';
import basketActions from '../basket/actions';
import conveyanceModeActions from '../conveyanceMode/actions';
import globalActions from '../global/actions';
import meActions from '../me/actions';
import orderStatusActions from '../orderStatus/actions';
import actions from './actions';
import { fireGACommerceLoginEvent } from '@/analytics/commerce/google';
import { genericEventHandler } from '@/analytics/events';
import { GlobalEvents } from '@/analytics/events/constants';
import { CAPTCHA_CONFIG, DEFAULT_PASSWORD_LOGIN_EXTRA_PAYLOAD } from '@/constants/authentication';
import { ERROR_MESSAGES } from '@/constants/events';
import { ROUTES } from '@/constants/routes';
import {
  clearExternalCookie,
  getExternalCookie,
  isExternalUserTokenValid,
  login,
} from '@/services/auth.service';
import { createHttpClient, resetLoyaltyToken } from '@/services/client';
import { type RootState } from '@/types/app';
import { type IUserLogin } from '@/types/authentication';
import { getOrigin } from '@/utils';
import { isAndroidShell } from '@/utils/android';
import { addOptionalPushNotificationToken } from '@/utils/auth';
import {
  assembleDeliveryObjectFromState,
  getLocationFromState,
  getIdsFromState,
} from '@/utils/checkout';
import { prepareErrorMessage } from '@/utils/global';
import { clearSavedCart } from '@/utils/savedCart';
import * as ErrorReporter from '@/utils/errorReporter';
import { getDuplicateAccountKind, isDeliveryUnavailableError } from '@/utils/errors';
import {
  GATEWAY_STATUSES,
  isNetworkError,
  isNonJsonResponseError,
  isStatusError,
} from '@/utils/networkErrors';

/**
 * How long to wait for the identity fetch after signing in mid-checkout before giving up
 * on the re-validate (TEAM-14627). Generous: missing it only costs the save-card option.
 */
const ME_IDENTITY_TIMEOUT_MS = 10_000;

export function* fetchUserSaga(action: ReturnType<typeof actions.login>) {
  const state = (yield select()) as RootState;
  const { basketId, locationId } = getIdsFromState(state);

  try {
    /** @TODO reconcile `LoyaltyUserRegistrationInfo` and `IUserLogin`. */
    // @ts-expect-error
    const request = addOptionalPushNotificationToken(action.params);

    // Authenticate against the API
    const origin = getOrigin(window.location.host);
    const client = createHttpClient({ origin });

    yield call(login, request, origin);

    const isMidCheckout = Boolean(action.currentPath?.includes(ROUTES.CHECKOUT) && basketId);

    if (isMidCheckout) {
      /**
       * TEAM-14320: this re-validate gets its own catch because the token has already
       * been minted by the time it runs — the diner IS signed in. Sharing the outer
       * catch sent a validate outage down the login branch, which cleared the freshly
       * authenticated user (`authFailure` + `clearMe`) and raised the LOGIN banner, on
       * /checkout, where no login form is even mounted.
       *
       * A transport-class failure here is the same failure `validateOrderSaga` routes to
       * the checkout recovery modal, and the diner is on /checkout, so it goes to the
       * same place. The login itself succeeded, so the flow continues past this block —
       * nothing is rethrown and nothing is aborted.
       *
       * `orderPending()` stays inside the `try`: a failed validate must fail CLOSED
       * (docs/CHECKOUT_PAGE.md, TEAM-14666), so checkout is never re-enabled on a basket
       * whose validity is unknown.
       *
       * Everything else rethrows into the outer catch, so server-sent errors keep the
       * byte-for-byte toast behaviour they had before this split — except on the chained
       * sign-up path, see below.
       *
       * TEAM-15137: `signUpChain` is carved out of that rethrow. The outer catch's
       * `signUpChain` branch is written for "the account was created but the LOGIN failed":
       * it pushes to /login with "Please login to continue.", then `authFailure` + `clearMe`.
       * Running that for a basket error would sign out a diner who IS authenticated, bounce a
       * successful checkout Join off /checkout (TEAM-13451 AC#4) and file the basket failure
       * under `SIGNUP_LOGIN_FAILURE`. That combination became reachable the moment
       * `createUserSaga` stopped aborting on its own validate failure (same ticket), because
       * a deterministic basket error — `option_not_on_menu`, unavailable items — fails again
       * here on the very next call.
       *
       * Falling through instead hands the error to its real owner: the post-identity
       * `validateOrder` below (TEAM-14627) runs it through `runBasketValidation`, which is
       * where the unavailable-items notice, the delivery/restaurant-unavailable modals and —
       * for a class it recognises none of, `option_not_on_menu` included — the checkout status
       * bar carrying coo-api's own sentence all live (same ticket). That handler owns the CTA
       * state too: it holds VALIDATING_ORDER while it runs, so the diner is told why AND cannot
       * submit. The point here is only that a basket error must stop being reported and rendered
       * as an auth failure. `!action.signUpChain` keeps the plain sign-in path byte-for-byte as
       * it was.
       */
      try {
        yield call(validateBasket, { basketId: basketId, locationId }, { client });
        yield put(orderStatusActions.orderPending());
      } catch (error) {
        const isTransportFailure = isNetworkError(error) || isNonJsonResponseError(error);

        if (!isTransportFailure && !action.signUpChain) {
          throw error;
        }

        if (isTransportFailure) {
          yield put(orderStatusActions.recovery502Triggered('validate'));
          ErrorReporter.reportLoginConnectionFailure(error, {
            route: ROUTES.CHECKOUT,
            urlHint: ErrorReporter.BASKET_VALIDATE_URL_HINT,
            basketId: basketId ? String(basketId) : undefined,
            locationId: locationId ? String(locationId) : undefined,
          });
        } else {
          // Reported as what it is — a basket validation failure, under the same event
          // `runBasketValidation` uses — rather than as an auth failure.
          ErrorReporter.captureException(error, 'error', {
            eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
          });
        }
      }
    }

    // Push to the url location that user tried to enter before being
    const url =
      action.referrer && Array.isArray(action.referrer) ? action.referrer[0] : action.referrer;
    if (url) {
      void Router.push(url);
    }
    // TEAM-13451: after a successful Join at checkout the referrer is blanked, so there is no
    // navigation here — the diner stays on /checkout (signed-in) to finish paying. The
    // double-submit is prevented without navigating: the in-flight `submitting` lock keeps the
    // button disabled through the whole flow, and the form unmounts once `me.id` is set
    // (checkout/account/index.tsx gates the Join tab on isGuestOrUser).

    // // dispatch AUTH_SUCCESS
    yield put(actions.authSuccess());

    // Fetch User Details
    yield put(meActions.fetchMe());

    yield put(globalActions.generateFeatureBag());

    // Generic Events
    genericEventHandler(GlobalEvents.AUTH__SIGN_IN_SUCCESS);
    //@TODO: Surface the login SSO method - Apple, Facebook, Google, etc.
    fireGACommerceLoginEvent(action.params.flow_type === 'implicit' ? 'sso' : 'password');

    /**
     * TEAM-14627: a diner who signs in or joins mid-checkout is never offered the
     * save-payment-method option until they refresh.
     *
     * chowly-api decides whether Toast may offer to save a card from the basket's
     * customer — `registered_diner?` requires a numeric diner id — so a guest basket mints
     * a token-only payment intent and no save-card option appears. Two things leave the
     * basket looking like a guest's here: the validate above passes no customer context at
     * all, and the checkout page's own `validateOrderSaga` substitutes `DEFAULT_GUEST_USER`
     * while `me` is still null (`orderStatus/saga.ts:225,476`).
     *
     * So wait for the identity, then validate once more. `validateOrderSaga` recomputes
     * `me?.id ? null : guestUser`, so this pass carries the registered diner and
     * chowly-api's `customer_context_upgrade_required?` upgrades the intent in place.
     * Raced against ME_FAIL, ME_CONNECTION_ERROR (a network-layer failure, TEAM-14780),
     * ME_STOP_LOADING (the quiet exit in `fetchMeSaga` that emits neither success nor
     * failure) and a timeout, so a slow or failed identity fetch can never leave checkout
     * hanging. Every terminal outcome of `fetchMeSaga` must be listed here, or that
     * outcome waits out the full timeout.
     *
     * Two invariants to preserve if this is edited:
     *  - Do not introduce an async effect between the `put(fetchMe())` above and this
     *    race. redux-saga's scheduler queues `fetchMeSaga`'s synchronous puts until this
     *    generator suspends, so the `take`s are registered in time only while the gap
     *    stays synchronous.
     *  - `VALIDATE_ORDER` is registered with `takeLeading` (`orderStatus/saga.ts`), so a
     *    validate already in flight would swallow this one silently.
     */
    if (isMidCheckout) {
      const { identified } = (yield race({
        identified: take(meActions.ME_SUCCESS),
        failed: take(meActions.ME_FAIL),
        connectionFailed: take(meActions.ME_CONNECTION_ERROR),
        stopped: take(meActions.ME_STOP_LOADING),
        timedOut: delay(ME_IDENTITY_TIMEOUT_MS),
      })) as { identified?: unknown };

      if (identified) {
        /**
         * Skip when the diner has already applied a card. chowly-api replaces a CONFIRMED
         * payment intent on a customer upgrade and hands back a new `sessionSecret`, which
         * forces the iframe to be confirmed again — the same symptom TEAM-14626 covers for
         * tip changes. The reported flow joins BEFORE entering card details, so the fix
         * still lands where the ticket needs it; a diner who applies a card first keeps
         * their applied card and simply does not gain the save-card option this session.
         */
        const current = (yield select()) as RootState;
        const cardAlreadyApplied = Boolean(current.app.payment.toast?.created);

        if (!cardAlreadyApplied) {
          yield put(orderStatusActions.validateOrder(basketId, locationId));
        }
      }
    }
  } catch (error) {
    /**
     * TEAM-14320: a browser transport failure carries no server response, so
     * `prepareErrorMessage` below would stringify the raw exception into the toast —
     * the diner literally reads "Could not login:  TypeError: Failed to fetch
     * (https://prod-coo-api.chowly.io)". Bail out first and show the plain-language
     * retry banner instead.
     *
     * Unlike `createUserSaga`, this catch dispatches `authFailure()` LAST, so this
     * branch has to dispatch it (and `clearMe`) itself — returning without it strands
     * `loading: true` and the login button spins forever.
     *
     * Skipped when `signUpChain` is set: the account already exists on that path and
     * the saga deliberately routes to /login with "Please login to continue.", where
     * the login form is not yet mounted to render a banner.
     */
    if (!action.signUpChain && isNetworkError(error)) {
      yield put(actions.authFailure());
      yield put(meActions.clearMe());
      yield put(actions.loginConnectionFailure());
      /**
       * Always the login endpoint. This branch is now reachable only from
       * `call(login, …)`: the mid-checkout re-validate handles its own transport failures
       * above and rethrows only non-transport errors, which this `isNetworkError` gate
       * rejects. Everything else left in the outer `try` after the login is `put`/`select`
       * effects, a floating `void Router.push` and the two analytics handlers — none of
       * which produce a fetch-transport `Error`.
       */
      ErrorReporter.reportLoginConnectionFailure(error, {
        route: ROUTES.LOGIN,
        urlHint: ErrorReporter.LOGIN_URL_HINT,
        basketId: basketId ? String(basketId) : undefined,
        locationId: locationId ? String(locationId) : undefined,
      });
      return;
    }

    // Error Notification
    // @todo this isn't of much use since the koala-sdk-js returns a static '401 Need to Refresh or Reauthenticate' message for 401 status.
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'We could not sign you in. Please check your details and try again.',
      error,
    );
    if (isDeliveryUnavailableError(errorResponse)) {
      yield put(orderStatusActions.deliveryUnavailable());
    }
    // TEMP WORKAROUND FOR CAPTCHA LIMITATION
    if (!action.signUpChain) {
      yield put(globalActions.displayErrorToast(errorResponse.message, false));
    } else {
      // If the login call fails after an account has been created successfully,
      // push them to login. This is likely a recaptcha issue
      yield Router.push(ROUTES.LOGIN);
      yield put(globalActions.displayToast('Please login to continue.'));
      ErrorReporter.reportAuthFailure(error, errorResponse, {
        eventName: ERROR_MESSAGES.SIGNUP_LOGIN_FAILURE,
        request: action.params,
      });
    }
    yield put(actions.authFailure());
    yield put(meActions.clearMe());
    ErrorReporter.reportAuthFailure(error, errorResponse, {
      eventName: ERROR_MESSAGES.LOGIN_FAILURE,
      request: action.params,
    });
  }
}

export function* createUserSaga(action: ReturnType<typeof actions.signUp>) {
  const state = (yield select()) as RootState;
  const { basketId, locationId } = getIdsFromState(state);

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const request = addOptionalPushNotificationToken(action.params);

    // Register the user
    yield call(register, request, { client });
    yield put(actions.signUpSuccess());

    const identifier = action.params.email;
    const secret = action.params.password;

    if (action.currentPath?.includes(ROUTES.CHECKOUT) && basketId) {
      /**
       * TEAM-15137: the Join-at-checkout basket validate gets its own boundary, for the same
       * reason the mid-checkout re-validate in `fetchUserSaga` does (TEAM-14320) — it runs
       * AFTER `register` has returned, so the account already exists by the time it can fail.
       *
       * Sharing the registration catch below sent every basket failure down the sign-up
       * branch: `signUpFailure()` + `toggleEmailVerification(false)` for an account that was
       * created, no chained `login(...)` and no "Account successfully created." toast (so the
       * diner was left holding an account they were never signed into), a
       * `CREATE_ACCOUNT_FAILURE` in Sentry for a basket error, and — since TEAM-15137 started
       * rendering the resolved copy — basket-flavoured diner copy ("refresh the menu…" for
       * `option_not_on_menu`) on what reads as a sign-up error.
       *
       * Nothing is rethrown: the sign-up itself succeeded, so the flow continues to the login
       * and the success toast. The failure is routed by class instead:
       *
       *  - Transport class → the checkout recovery modal, the same surface `runBasketValidation`
       *    sends it to (`orderStatus/saga.ts`, docs/CHECKOUT_PAGE.md "Service unreachable /
       *    non-JSON edge error → recovery modal"). The 502 disjunct is carried over from
       *    `runBasketValidation` rather than from the `fetchUserSaga` sibling, which omits it
       *    only because everything it does not catch rethrows into a catch that toasts — here
       *    nothing rethrows, so an unrouted 502 would be silent.
       *  - Everything else (a server-sent envelope: unavailable items, an invalid basket) is
       *    reported under the same `BASKET_VALIDATION_ERROR` event `runBasketValidation` uses,
       *    and left to that handler: the chained login below ends in `fetchUserSaga`'s
       *    post-identity `validateOrder` (TEAM-14627), which runs `runBasketValidation` —
       *    the unavailable-items notice, the delivery/restaurant-unavailable modals, and the
       *    checkout status bar for anything else it can resolve copy for (`option_not_on_menu`
       *    among them, same ticket). What must not happen is what used to — the basket failure is
       *    not a sign-up failure and must not be rendered or reported as one. Note the handoff is
       *    what gates the CTA: THIS catch only declines to re-arm it, and the post-identity
       *    re-validate is skipped when a card is already applied or the identity fetch fails, so
       *    on those paths the failure is reported and not shown. Reaching that handler at all
       *    depends on the `signUpChain` carve-out in `fetchUserSaga`'s re-validate catch above;
       *    without it the same error lands in the login catch and redirects to /login.
       *
       * `orderPending()` stays inside the `try`: a failed validate must fail CLOSED
       * (docs/CHECKOUT_PAGE.md, TEAM-14666), so checkout is never re-enabled on a basket whose
       * validity is unknown.
       */
      try {
        yield call(validateBasket, { basketId: basketId, locationId }, { client });
        yield put(orderStatusActions.orderPending());
      } catch (error) {
        if (
          isStatusError(error, GATEWAY_STATUSES) ||
          isNetworkError(error) ||
          isNonJsonResponseError(error)
        ) {
          yield put(orderStatusActions.recovery502Triggered('validate'));
        }

        ErrorReporter.captureException(error, 'error', {
          eventName: ERROR_MESSAGES.BASKET_VALIDATION_ERROR,
        });
      }
    }

    // Login User - global input keys
    const loginPayload: IUserLogin = {
      ...DEFAULT_PASSWORD_LOGIN_EXTRA_PAYLOAD,
      identifier,
      secret,
    };

    // Login User - create the payload with the organizations optional extra payload
    // Pass the captcha_response from sign up
    const payload = {
      ...loginPayload,
      [CAPTCHA_CONFIG.FIELD_NAME]: action.params.login_captcha_response ?? undefined,
    };

    // `currentPath` is the 4th parameter of `actions.login`; dropping it here left every
    // diner who JOINS mid-checkout skipping the checkout blocks in `fetchUserSaga` — which
    // is the exact flow TEAM-14627 reports.
    yield put(actions.login(payload, action.referrer, true, action.currentPath));

    // Success Notification
    yield put(globalActions.displayToast('Account successfully created.'));

    // Generic Events
    genericEventHandler(GlobalEvents.AUTH__SIGN_UP_SUCCESS);
  } catch (error) {
    // Error Notification
    yield put(actions.signUpFailure());

    yield put(actions.toggleEmailVerification(false));

    if (isNetworkError(error)) {
      yield put(actions.signUpConnectionFailure());
      ErrorReporter.reportRegisterConnectionFailure(error, {
        route: '/sign-up',
        urlHint: ErrorReporter.REGISTER_URL_HINT,
        basketId: basketId ? String(basketId) : undefined,
        locationId: locationId ? String(locationId) : undefined,
      });
      return;
    }

    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      // Tier-4 static: what the diner reads when nothing in the envelope resolves (TEAM-15137).
      "We couldn't create your account. Please try again.",
      error,
    );
    if (isDeliveryUnavailableError(errorResponse)) {
      yield put(orderStatusActions.deliveryUnavailable());
    }

    // TEAM-13882: one shared predicate, reading the raw upstream text rather than the resolved
    // diner copy. `errorReporter.reportAuthFailure` below uses the same function to decide the
    // Sentry severity, so the two can no longer drift.
    const duplicateAccountKind = getDuplicateAccountKind(errorResponse.detail);

    if (duplicateAccountKind) {
      yield put(actions.signUpDuplicateAccount());
      yield put(
        globalActions.displayToast(
          duplicateAccountKind === 'email'
            ? 'This email is already associated with an account. Please sign in instead.'
            : 'This phone number is already associated with an account. Please sign in instead.',
        ),
      );
    } else {
      // TEAM-15137: render the resolved copy, like every other saga in this file. This used to
      // substitute a hardcoded sentence instead, which discarded `user_message` and every
      // catalogued code or slug — sign-up was the one route where no envelope field could reach
      // the diner. TEAM-13451 AC#5 (never put the raw payload on screen) is not weakened by this:
      // `resolveDinerMessage` closes that leak by construction — it never returns
      // `error_description` or the bare `error` slug, and HTML-escapes the upstream copy it does
      // return, so anything unmapped falls through to the static above rather than leaking.
      yield put(globalActions.displayErrorToast(errorResponse.message, true));
    }

    ErrorReporter.reportAuthFailure(error, errorResponse, {
      eventName: ERROR_MESSAGES.CREATE_ACCOUNT_FAILURE,
      request: action.params,
    });
  }
}

function* reloadUserSaga() {
  // When you reload the page the user appears logged out.
  // This sequence will restore the login state or ensure the
  // token is reset for a new user.
  const origin = getOrigin(window.location.host);
  const externalCookie = getExternalCookie();

  if (externalCookie && !isExternalUserTokenValid()) {
    try {
      const { cookie, provider } = externalCookie;
      const payload = {
        provider,
        flow_type: 'implicit',
        identifier: cookie,
        secret: cookie,
      };

      yield call(login, payload, origin);
    } catch (error) {
      ErrorReporter.reportAuthFailure(error, null, {
        eventName: ERROR_MESSAGES.RESTART_USER_SESSION_FAILURE,
        message: 'External cookie session restart failed',
      });
    } finally {
      yield call(clearExternalCookie);
    }
  }

  if (validateLoyaltyToken(origin)) {
    try {
      // Fetch User Details
      yield put(meActions.fetchMe());
    } catch (error) {
      // Clear user
      yield put(meActions.clearMe());
      const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
        prepareErrorMessage,
        'It looks like there was an error restarting your user session.',
        error,
      );
      yield put(globalActions.displayErrorToast(errorResponse.message, true));
    }
  }
}

function* doLogoutSaga(action: ReturnType<typeof actions.logout>) {
  try {
    const origin = getOrigin(window.location.host);
    const client = createHttpClient({ origin });

    yield call(logout, { client });
    yield resetLoyaltyToken({ origin });

    // Clear user
    yield put(meActions.clearMe());

    // Clear user specific data from cart
    yield put(basketActions.depersonalizeBasket());

    // Clear things like rewards from cart
    yield put(orderStatusActions.destroyOrder());

    const state: RootState = yield select();
    const conveyanceMode = state.app.conveyanceMode.type;

    // On logout, clear ID and default (user-only properties) from delivery address
    if (conveyanceMode === CONVEYANCE_TYPES.DELIVERY) {
      const deliveryAddress = assembleDeliveryObjectFromState(state);
      if (deliveryAddress?.id) {
        const location = getLocationFromState(state);
        const loggedOutAddress = Object.assign({}, deliveryAddress);
        delete loggedOutAddress.id;
        delete loggedOutAddress.default;

        /**
         * @TODO reconcile IConveyanceDeliveryAddress and
         * the SDK `DeliveryAddress` types.
         */
        yield put(
          conveyanceModeActions.setDeliveryAddress(
            // @ts-expect-error
            loggedOutAddress,
            location,
            false,
          ),
        );
      }
    }

    // Fire Generic Event
    genericEventHandler(GlobalEvents.AUTH__CLICK_LOGOUT, {
      name: Router.pathname,
    });

    // Clear basket data
    yield put(basketActions.destroyBasket());
    // TEAM-15276: including a cart saved for later — it can carry a delivery address.
    clearSavedCart();

    // Success
    yield put({ type: actions.LOGOUT_SUCCESS });

    // Push to homepage
    if (!action.cancelRedirect) {
      if (isAndroidShell()) {
        yield Router.push(ROUTES.ANDROID);
      } else {
        yield Router.push(ROUTES.HOMEPAGE);
      }
    }
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error logging you out. Please try again',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Stop loading
    yield put({ type: actions.LOGOUT_FAILURE });

    // If error results from a stored address, record the address ID
    const state: RootState = yield select();
    const deliveryAddress = assembleDeliveryObjectFromState(state);

    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.LOGOUT_FAILURE,
      details: errorResponse,
      additionalDetails: deliveryAddress?.id
        ? `Stored Address ID: ${deliveryAddress.id}`
        : 'No stored address ID',
    });
  }
}

export function* forgotPasswordSaga(action: ReturnType<typeof actions.forgotPassword>) {
  // sends email
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(forgotPassword, action.email, { client });

    // Notify success
    yield put(
      globalActions.displayToast(
        'Please check your email for instructions on how to reset your password.',
      ),
    );
    // Stop loading state
    yield put(actions.forgotPasswordSuccess());
  } catch (error) {
    /**
     * TEAM-14320: same raw-exception leak as login — `prepareErrorMessage` would append
     * "TypeError: Failed to fetch (https://prod-coo-api.chowly.io)" to the toast. Show
     * the retryable connection banner instead. Server-sent errors keep their toast.
     */
    if (isNetworkError(error)) {
      yield put(actions.forgotPasswordConnectionFailure());
      ErrorReporter.reportForgotPasswordConnectionFailure(error, {
        route: ROUTES.FORGOT_PASSWORD,
        urlHint: ErrorReporter.FORGOT_PASSWORD_URL_HINT,
      });
      return;
    }

    yield put(actions.forgotPasswordFail());
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'It looks like there was an error processing your request. ',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.FORGOT_PASSWORD_FAILURE,
      details: errorResponse,
    });
  }
}

function* resetPasswordSaga(action: any) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(resetPassword, action.params, { client });
    yield put({ type: actions.RESET_PASSWORD_SUCCESS });
    yield put(globalActions.displayToast('Your password has been successfully reset.'));
    yield Router.push(ROUTES.LOGIN);
  } catch (error) {
    yield put({ type: actions.RESET_PASSWORD_FAIL });
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'Failed to reset password. ',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.PASSWORD_RESET_FAILURE,
      details: errorResponse,
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.RELOAD_USER, reloadUserSaga);
  yield takeLatest(actions.LOGIN_REQUEST, fetchUserSaga);
  yield takeLatest(actions.SIGN_UP_REQUEST, createUserSaga);
  yield takeLatest(actions.FORGOT_PASSWORD, forgotPasswordSaga);
  yield takeLatest(actions.RESET_PASSWORD, resetPasswordSaga);
  yield takeLatest(actions.LOGOUT, doLogoutSaga);
}
